import _ from "lodash";
import React from "react";

import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import { FormTextArea } from "@/component-library/FormFields/FormTextArea/FormTextArea";
import {
  INTERVENTION_AND_PLAN_CHARACTER_LIMIT,
  INTERVENTION_AND_PLAN_CHARACTER_LIMIT_ERROR,
} from "@/features/notes/(v2)/constants/intervention-and-plan-v2-form-schema";
import { makeCnaiProps } from "@/lib/helpers/clinically-ai";
import { Text, TextTypes } from "@/component-library";

// isCnaiEnabled: this component derives its own cnai field id from the goal, so callers can't pass a
// field descriptor in — they opt this instance in instead. Only one rendered instance per note may
// be tagged, otherwise the same cnai-template-field-id appears twice in one template container.
export function InterventionAndPlanNoteField({
  editable,
  editHandler,
  formikProps,
  goalNumber,
  isCnaiEnabled,
  isEditing,
}) {
  const hasCharacterLimitErrorAndEditable =
    editable &&
    formikProps.errors?.[goalNumber]?.interventions_and_plans ===
      INTERVENTION_AND_PLAN_CHARACTER_LIMIT_ERROR;

  if (!isEditing) {
    return (
      <>
        <Text
          className={
            hasCharacterLimitErrorAndEditable
              ? "MemberProfileGoalsAndObjectiveNoteDisplayV2--form_field_view_only_error"
              : "MemberProfileGoalsAndObjectiveNoteDisplayV2--form_field_view_only"
          }
          type={TextTypes.paragraph}
        >
          {formikProps.values[goalNumber].interventions_and_plans}
        </Text>

        {hasCharacterLimitErrorAndEditable && (
          <CharacterCounter
            characterLimit={INTERVENTION_AND_PLAN_CHARACTER_LIMIT}
            length={formikProps.values[goalNumber].interventions_and_plans.length}
          />
        )}
      </>
    );
  }

  return (
    <>
      <FormTextArea
        formikProps={formikProps}
        inputClassName="MemberProfileGoalsAndObjectiveNoteDisplayV2--form_field"
        name={`${goalNumber}.interventions_and_plans`}
        onChangeCallBack={async (name, value) => {
          const dataToSave = _.cloneDeep(formikProps.values);

          delete dataToSave["isEditingInternal"];
          dataToSave[goalNumber] = {
            ...dataToSave[goalNumber],
            interventions_and_plans: value,
          };

          editHandler("goals_objectives_progress_v2", dataToSave);
        }}
        testId="interventionAndPlanTextArea"
        textareaProps={
          isCnaiEnabled
            ? makeCnaiProps({
                // goal_id can be missing on some goal records; goalNumber is the object key this
                // goal is stored under (see MemberProfileGoalsAndObjectiveNoteDisplayV2.js), so
                // it's always defined and unique across the map — falling back to it instead of
                // an interpolated undefined avoids every goal-id-less goal colliding on the same
                // "interventions-and-plans-undefined" id (see clinically-ai.ts's empty-id note)
                id: `interventions-and-plans-${formikProps.values[goalNumber]?.goal_id ?? goalNumber}`,
                name: "Interventions & Plans",
                type: "string",
              })
            : undefined
        }
        triggerErrorOnChange
      />

      {formikProps.errors?.[goalNumber]?.interventions_and_plans ===
        INTERVENTION_AND_PLAN_CHARACTER_LIMIT_ERROR && (
        <CharacterCounter
          characterLimit={INTERVENTION_AND_PLAN_CHARACTER_LIMIT}
          length={formikProps.values[goalNumber].interventions_and_plans.length}
        />
      )}
    </>
  );
}
